import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Inbox, PowerOff, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * The states every service page shares — loading, empty, unavailable, error,
 * success — drawn one way so a guest learns them once.
 */

interface StateViewProps {
  icon: LucideIcon;
  title: string;
  message?: string;
  tone?: 'neutral' | 'error' | 'success';
  children?: ReactNode;
  className?: string;
}

export function StateView({
  icon: Icon,
  title,
  message,
  tone = 'neutral',
  children,
  className,
}: StateViewProps) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex flex-col items-center px-6 py-14 text-center', className)}
    >
      <div
        className={cn(
          'mb-4 flex size-16 items-center justify-center rounded-full',
          tone === 'error' && 'bg-destructive/10 text-destructive',
          tone === 'success' && 'bg-success/15 text-success',
          tone === 'neutral' && 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="size-8" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold">{title}</h2>
      {message && <p className="text-muted-foreground mt-1 max-w-xs text-sm">{message}</p>}
      {children && <div className="mt-6 flex w-full max-w-xs flex-col gap-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ title, message }: { title: string; message?: string }) {
  return <StateView icon={Inbox} title={title} message={message} />;
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <StateView icon={AlertTriangle} title={title} message={message} tone="error">
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          Try again
        </Button>
      )}
      <Button asChild variant="ghost">
        <Link to="/">Back to home</Link>
      </Button>
    </StateView>
  );
}

/** The service is switched off for this hotel — a stale link or a change since the page loaded. */
export function UnavailableState({ service }: { service: string }) {
  return (
    <StateView
      icon={PowerOff}
      title={`${service} is not available`}
      message="This service is not offered at the moment. Reception will be happy to help."
    >
      <Button asChild>
        <Link to="/call">Call Reception</Link>
      </Button>
      <Button asChild variant="ghost">
        <Link to="/">Back to home</Link>
      </Button>
    </StateView>
  );
}

export function SuccessState({
  title,
  message,
  children,
}: {
  title: string;
  message?: string;
  children?: ReactNode;
}) {
  return (
    <StateView icon={CheckCircle2} title={title} message={message} tone="success">
      {children}
      <Button asChild variant="outline">
        <Link to="/requests">View my requests</Link>
      </Button>
      <Button asChild variant="ghost">
        <Link to="/">Back to home</Link>
      </Button>
    </StateView>
  );
}

/** Placeholder rows while a catalogue loads. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3 p-4" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}
